'use client';

import {
  MouseEvent,
  MutableRefObject,
  useEffect,
  useRef,
  useState
} from 'react';
import Slider from 'react-slick';
import { ReactComponent as ChevronIcon } from '@assets/icons/caret-right.svg';
import { defaultSettings } from 'constant';

import { Box, Flex, IconButton } from '@chakra-ui/react';

import { invertColor } from 'utils';

import { SiteAlertsInterface, SiteAlertType } from 'types';

import { Link, Text } from 'ui';

interface BannerViewProps {
  banners: SiteAlertsInterface[];
  onBannerClick: (idx: number) => void;
}

const BannerView = ({ banners, onBannerClick }: BannerViewProps) => {
  let sliderRef = useRef<MutableRefObject<Slider | null>>(null);
  const [slideIndex, setSlideIndex] = useState<number>(0);

  const currentBanner = banners[slideIndex];

  const next = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    // @ts-ignore no ts documentation for react-slider package
    sliderRef?.slickNext();
  };
  const previous = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    // @ts-ignore no ts documentation for react-slider package
    sliderRef?.slickPrev();
  };

  const settings = {
    ...defaultSettings,
    beforeChange: (_: number, nextIndex: number) => setSlideIndex(nextIndex)
  };

  useEffect(() => {
    setSlideIndex(0);
  }, [banners.length]);

  const maxSizeDesktop = (size: number) => (size > 40 ? '40px' : size);
  const maxSizeMobile = (size: number) => (size > 29 ? '29px' : size);

  const maxFontSize = Math.max(...banners.map((o) => o.font_size));

  return banners.length === 1 ? (
    <Flex
      bg={`#${currentBanner?.fill_color}`}
      w='100vw'
      justifyContent='center'
    >
      <Flex
        pl={{ base: '16px', lg: '25px' }}
        px={{ base: '16px', lg: '25px' }}
        alignItems='center'
        as={Link}
        external={currentBanner?.site_alert_type === SiteAlertType.UrlNewTab}
        onClick={() => onBannerClick(slideIndex)}
        to={currentBanner?.url ?? ''}
        maxW='container.xl'
        justifyContent='center'
        py={2}
      >
        <Text
          sx={{
            lineHeight: 'normal',
            WebkitLineClamp: { base: 2, lg: 1 },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical'
          }}
          fontSize={{
            base: maxSizeMobile(currentBanner?.font_size),
            md: maxSizeDesktop(currentBanner?.font_size)
          }}
          textAlign='center'
          color={`#${currentBanner?.font_color}`}
        >
          {currentBanner?.text}
        </Text>
      </Flex>
    </Flex>
  ) : (
    <Slider
      ref={(slider) => {
        // @ts-ignore no ts documentation for react-slider package
        sliderRef = slider as Slider;
      }}
      fade
      {...settings}
      adaptiveHeight
    >
      {banners.map((banner) => (
        <Box key={banner.id} p={0} bg={`#${banner.fill_color}`} h='full'>
          <Flex
            justifyContent='center'
            maxW='container.xl'
            margin='0 auto'
            flexDirection={{ base: 'column', lg: 'row' }}
            alignItems={{ base: 'flex-end', lg: 'center' }}
          >
            <Flex
              cursor='pointer'
              pt={2}
              pb={{ base: 0, lg: 2 }}
              px={{ base: '16px', lg: '25px' }}
              alignItems='center'
              onClick={() => onBannerClick(slideIndex)}
              w='full'
              justifyContent='center'
              minH={{
                base: `${maxFontSize * 2 + 26}px`,
                lg: `${maxFontSize + 26}px`
              }}
            >
              <Text
                sx={{
                  WebkitLineClamp: { base: 2, lg: 1 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical'
                }}
                fontSize={{
                  base: maxSizeMobile(banner.font_size),
                  md: maxSizeDesktop(banner.font_size)
                }}
                textAlign='center'
                color={`#${banner?.font_color}`}
              >
                {banner.text}
              </Text>
            </Flex>
            <Flex bottom={0} w='auto' justifyContent='center' h='full'>
              <Flex
                w='full'
                right={0}
                zIndex={99}
                justifyContent='flex-end'
                gap={3}
                alignItems='center'
                pr='25px'
              >
                <Flex>
                  <IconButton
                    onClick={previous}
                    bg='transparent'
                    aria-label='open appointment details'
                    minW={0}
                    width='auto'
                    opacity={0.35}
                    _hover={{
                      opacity: 1
                    }}
                    icon={
                      <ChevronIcon
                        style={{
                          transform: 'rotate(180deg)'
                        }}
                        fill={invertColor(banner.fill_color)}
                        width={24}
                        height={24}
                      />
                    }
                  />
                  <IconButton
                    onClick={next}
                    bg='transparent'
                    aria-label='open appointment details'
                    minW={0}
                    width='auto'
                    opacity={0.35}
                    _hover={{
                      opacity: 1
                    }}
                    icon={
                      <ChevronIcon
                        style={{
                          transform: 'rotate(0deg)'
                        }}
                        fill={invertColor(banner.fill_color)}
                        width={24}
                        height={24}
                      />
                    }
                  />
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Box>
      ))}
    </Slider>
  );
};

export default BannerView;
